"use client";

/**
 * NavDropdown
 * Hover-based dropdown menu for the navbar.
 * Shows child links in a floating panel on hover (desktop).
 * In builder mode, renders non-interactive label with chevron.
 */

import type React from "react";
import { useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { scrollToAnchor } from "../../../../_logic/utils/scroll-utils";
import { NAV_DROPDOWN_ANCHOR_GAP_PX } from "../../../../_logic/constants";
import { useNavDisclosure } from "../../../../_hooks/useNavDisclosure";

/** Single link inside a dropdown */
export interface NavDropdownChild {
  label: string;
  id: string;
}

interface NavDropdownProps {
  /** Dropdown trigger label */
  label: string;
  /** Child links rendered in the dropdown panel */
  children: NavDropdownChild[];
  /** Whether this is the public (interactive) view */
  isPublic: boolean;
}

/** Hover dropdown for the navbar — opens on mouse enter, closes with 150ms delay */
export function NavDropdown({ label, children, isPublic }: NavDropdownProps): React.ReactElement {
  // States
  const { isOpen, containerRef, containerProps, triggerProps, close } = useNavDisclosure();

  // Handlers
  const handleAnchorClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
      e.preventDefault();
      scrollToAnchor(sectionId, NAV_DROPDOWN_ANCHOR_GAP_PX);
      close();
    },
    [close]
  );

  // Builder mode — non-interactive
  if (!isPublic) {
    return (
      <span className="flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-gray-600">
        {label}
        <ChevronDown className="h-3.5 w-3.5" />
      </span>
    );
  }

  // Public mode — hover dropdown
  return (
    <div className="relative" ref={containerRef} {...containerProps}>
      <button
        type="button"
        {...triggerProps}
        className="flex touch-manipulation items-center gap-1 whitespace-nowrap text-sm font-semibold text-gray-600 transition-colors hover:text-gray-900"
        aria-haspopup="menu"
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-full z-50 mt-1 min-w-[180px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          role="menu"
        >
          {children.map((child) => (
            <a
              key={child.id}
              href={`#${child.id}`}
              onClick={(e) => handleAnchorClick(e, child.id)}
              className="block touch-manipulation px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
              role="menuitem"
            >
              {child.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
